import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { breadcrumbSchema, itemListSchema } from "@/components/JsonLd";
import { useSiteContent } from "@/context/SiteContentContext";

const REGION_ORDER = [
  "North India",
  "South India",
  "North India / Himalaya",
  "Himalaya / Extension",
  "Middle East / Extension",
  "Southeast Asia / Extension",
];

export default function Destinations() {
  const c = useSiteContent("destinations");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/destinations");
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group by region in preferred order
  const grouped = useMemo(() => {
    const byRegion = {};
    items.forEach((d) => {
      if (!byRegion[d.region]) byRegion[d.region] = [];
      byRegion[d.region].push(d);
    });
    const ordered = [];
    REGION_ORDER.forEach((r) => {
      if (byRegion[r]) ordered.push([r, byRegion[r]]);
    });
    Object.keys(byRegion).forEach((r) => {
      if (!REGION_ORDER.includes(r)) ordered.push([r, byRegion[r]]);
    });
    return ordered;
  }, [items]);

  return (
    <PageLayout>
      <Seo
        title="Destinations — Where will your story unfold?"
        description="Explore India's iconic cities — Agra, Jaipur, Delhi, Varanasi, Kerala, Ladakh — plus Nepal, Bhutan, Dubai and Thailand. Discover the place that calls to you."
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Destinations", path: "/destinations" },
          ]),
          itemListSchema({
            name: "All Destinations — Tour Trips India Holidays",
            items: items.map((d) => ({ name: d.name, url: `/destinations/${d.slug}` })),
          }),
        ]}
      />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-24 md:py-32" data-testid="destinations-page">
        <div className="container-tti">
          {loading ? (
            <div className="text-center text-foreground/60 py-20">Loading destinations…</div>
          ) : (
            grouped.map(([region, list], idx) => (
              <div key={region} className={idx > 0 ? "mt-24" : ""}>
                <div className="grid lg:grid-cols-12 gap-12 mb-12">
                  <div className="lg:col-span-5">
                    <div className="eyebrow mb-3">{region.includes("Extension") ? "Beyond India" : "Region"}</div>
                    <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">{region}</h2>
                  </div>
                  <div className="lg:col-span-7 flex items-end">
                    <p className="text-foreground/70 text-base font-light">
                      {list.length} curated destinations within this region. Click any to read more or send us an inquiry.
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/destinations/${d.slug}`}
                      data-testid={`destination-card-${d.slug}`}
                      className="group block bg-white border border-cream-200 rounded-sm overflow-hidden hover:shadow-2xl hover:-translate-y-1 hover:border-marigold/40 transition-all duration-500 ease-out"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src={d.image_url}
                          alt={d.name}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-deepred text-white text-[10px] uppercase tracking-[0.25em] font-medium rounded-sm">
                            {d.country}
                          </span>
                        </div>
                        <div className="absolute bottom-0 inset-x-0 p-6">
                          <h3 className="font-serif text-3xl text-white tracking-tight leading-tight">{d.name}</h3>
                          <p className="text-xs uppercase tracking-[0.2em] text-marigold mt-1.5">{d.tagline}</p>
                        </div>
                      </div>
                      <div className="p-5">
                        <p className="text-sm text-foreground/75 font-light leading-relaxed mb-4">
                          {d.summary.length > 130 ? d.summary.slice(0, 130) + "…" : d.summary}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground/60">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-saffron" /> {d.best_time}
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-saffron group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </PageLayout>
  );
}
